/**
 * Initials shown on the account avatar, mirroring the logic every current
 * header component duplicates (see e.g. the super-user and customer-app
 * `HeaderComponent.getInitials()`), pulled into one pure function so
 * `sh-app-shell` and `sh-account-panel` don't have to re-implement it.
 *
 * - Both a given name and a surname: first letter of each.
 * - Only one of the two: its first two letters.
 * - Neither: '{ }' (the existing fallback for "no identity yet").
 */
export function accountInitials(
  givenName?: string | null,
  surname?: string | null
): string {
  const hasGivenName = !!givenName && givenName.length > 0;
  const hasSurname = !!surname && surname.length > 0;

  if (hasGivenName && hasSurname) {
    return (
      (givenName as string).substr(0, 1) + (surname as string).substr(0, 1)
    ).toUpperCase();
  }

  if (hasGivenName) {
    return (givenName as string).substr(0, 2).toUpperCase();
  }

  if (hasSurname) {
    return (surname as string).substr(0, 2).toUpperCase();
  }

  return '{ }';
}

/**
 * Same initials logic, from a single `fullName` string (e.g. a
 * `StoredAccountInfo.fullName`), split on the first space into a given name
 * and surname.
 */
export function accountInitialsFromFullName(fullName?: string | null): string {
  if (!fullName) {
    return accountInitials();
  }

  const trimmed = fullName.trim();
  const spaceIndex = trimmed.indexOf(' ');

  if (spaceIndex === -1) {
    return accountInitials(trimmed);
  }

  return accountInitials(
    trimmed.substring(0, spaceIndex),
    trimmed.substring(spaceIndex + 1)
  );
}
